<template>
  <div class="fill-height home-page" >

    <transition name="standard">
      <AnimatedClouds @canplay.native="videoLoaded = true" v-show="videoLoaded" v-if="!$root.platform.prerenderer" />
    </transition>

    <transition>
      <ImagePlayer
        :key="$language.current"
        ref="player"
        v-if="videoLoaded"
        class="image-player"
        :class="{dragging: mouseDown}"
        :frames="playerFrames"
        :offset-x="playerOffsetX"
        :transparency-frames="transparencyFrames"
        :loading.sync="playerLoading"
        :load-progress.sync="playerLoadProgress"
        :currentFrame.sync="playerCurrentFrame"
        v-show="!playerLoading && !introTimer"
        @mousedown.native="onMouseDown"
        @mouseup.native="mouseDown = false"
        @mousemove.native="onMouseMove"
        @touchstart.native="onMouseDown"
        @touchend.native="mouseDown = false"
        @touchmove.native.prevent="onMouseMove"
        />
    </transition>

    <transition name="standard">
      <v-row class="fill-height" align="center" justify="center" align-content="center" v-if="playerLoading || introTimer || !videoLoaded"  >
        <h1 class="col col-12 text-center" style="z-index: 1;"><CharSplitText :style="{'--appear-delay': `100ms`}" :text="welcomeText" /></h1>
        <v-col class="pa-12 shrink"  style="z-index: 3;">
          <LoadingSpinner  />
        </v-col>
      </v-row>
    </transition>

    <transition name="standard"><div class="absolute clouds" style="z-index: 2;" /></transition>
    <transition name="standard"><div class="absolute clouds clouds--second" style="z-index: 2;" /></transition>

    <transition name="standard">
      <v-row no-gutters class="px-4 initial-tutorial" style="z-index: 4;" align="center" justify="center" v-if="!playerLoading && !introTimer && videoLoaded && introTutorial" >
        <ClickDragTutorial key="big" class="col shrink" big-version :style="{minWidth: '300px'}" />
      </v-row>
    </transition>

    <transition name="standard">
      <ClickDragTutorial key="little" class="bottom-tutorial"  style="z-index: 3;" v-if="!playerLoading && !introTimer && videoLoaded && !introTutorial" />
    </transition>
  </div>
</template>

<script>
import FontFaceObserver from 'fontfaceobserver'

import { Viewport } from '@monogrid/vue-lib'
import frames from '@/assets/img/sequences/test1-frames'
import LoadingSpinner from '@/atoms/LoadingSpinner'
import ImagePlayer from '@/molecules/ImagePlayer'
import * as PIXI from 'pixi.js'
import anime from 'animejs'
import delay from 'lodash/delay'
import { RAF, ElasticNumber } from '@monogrid/js-utils'
import hotspotSprite1 from '../assets/spritesheets/hotspot/hotspot_1000.png'
import hotspotSprite2 from '../assets/spritesheets/hotspot/hotspot_1001.png'
import hotspotSprite3 from '../assets/spritesheets/hotspot/hotspot_1002.png'
import hotspotSprite4 from '../assets/spritesheets/hotspot/hotspot_1003.png'
import hotspotSprite5 from '../assets/spritesheets/hotspot/hotspot_1004.png'
import hotspotSprite6 from '../assets/spritesheets/hotspot/hotspot_1005.png'
import hotspotSprite7 from '../assets/spritesheets/hotspot/hotspot_1006.png'
import hotspotSprite8 from '../assets/spritesheets/hotspot/hotspot_1007.png'
import hotspotSprite9 from '../assets/spritesheets/hotspot/hotspot_1008.png'
import hotspotSprite10 from '../assets/spritesheets/hotspot/hotspot_1009.png'
import hotspotSprite11 from '../assets/spritesheets/hotspot/hotspot_1010.png'
import hotspotSprite12 from '../assets/spritesheets/hotspot/hotspot_1011.png'
import hotspotSprite13 from '../assets/spritesheets/hotspot/hotspot_1012.png'
import hotspotSprite14 from '../assets/spritesheets/hotspot/hotspot_1013.png'
import hotspotSprite15 from '../assets/spritesheets/hotspot/hotspot_1014.png'
import hotspotSprite16 from '../assets/spritesheets/hotspot/hotspot_1015.png'
import hotspotSprite17 from '../assets/spritesheets/hotspot/hotspot_1016.png'
import hotspotSprite18 from '../assets/spritesheets/hotspot/hotspot_1017.png'
import hotspotSprite19 from '../assets/spritesheets/hotspot/hotspot_1018.png'
import hotspotSprite20 from '../assets/spritesheets/hotspot/hotspot_1019.png'
import hotspotSprite21 from '../assets/spritesheets/hotspot/hotspot_1020.png'
import hotspotSprite22 from '../assets/spritesheets/hotspot/hotspot_1021.png'
import hotspotSprite23 from '../assets/spritesheets/hotspot/hotspot_1022.png'
import hotspotSprite24 from '../assets/spritesheets/hotspot/hotspot_1023.png'
import hotspotSprite25 from '../assets/spritesheets/hotspot/hotspot_1024.png'
import hotspotSprite26 from '../assets/spritesheets/hotspot/hotspot_1025.png'
import hotspotSprite27 from '../assets/spritesheets/hotspot/hotspot_1026.png'
import hotspotSprite28 from '../assets/spritesheets/hotspot/hotspot_1027.png'
import hotspotSprite29 from '../assets/spritesheets/hotspot/hotspot_1028.png'
import hotspotSprite30 from '../assets/spritesheets/hotspot/hotspot_1029.png'
import hotspotSprite31 from '../assets/spritesheets/hotspot/hotspot_1030.png'
import hotspotSprite32 from '../assets/spritesheets/hotspot/hotspot_1031.png'
import hotspotSprite33 from '../assets/spritesheets/hotspot/hotspot_1032.png'
import hotspotSprite34 from '../assets/spritesheets/hotspot/hotspot_1033.png'
import hotspotSprite35 from '../assets/spritesheets/hotspot/hotspot_1034.png'
import hotspotSprite36 from '../assets/spritesheets/hotspot/hotspot_1035.png'
import hotspotSprite37 from '../assets/spritesheets/hotspot/hotspot_1036.png'
import hotspotSprite38 from '../assets/spritesheets/hotspot/hotspot_1037.png'
import hotspotSprite39 from '../assets/spritesheets/hotspot/hotspot_1038.png'
import hotspotSprite40 from '../assets/spritesheets/hotspot/hotspot_1039.png'
import hotspotSprite41 from '../assets/spritesheets/hotspot/hotspot_1040.png'
import hotspotSprite42 from '../assets/spritesheets/hotspot/hotspot_1041.png'
import hotspotSprite43 from '../assets/spritesheets/hotspot/hotspot_1042.png'
import hotspotSprite44 from '../assets/spritesheets/hotspot/hotspot_1043.png'
import hotspotSprite45 from '../assets/spritesheets/hotspot/hotspot_1044.png'
import hotspotSprite46 from '../assets/spritesheets/hotspot/hotspot_1045.png'
import hotspotSprite47 from '../assets/spritesheets/hotspot/hotspot_1046.png'
import hotspotSprite48 from '../assets/spritesheets/hotspot/hotspot_1047.png'
import hotspotSprite49 from '../assets/spritesheets/hotspot/hotspot_1048.png'
import hotspotSprite50 from '../assets/spritesheets/hotspot/hotspot_1049.png'
import hotspotSprite51 from '../assets/spritesheets/hotspot/hotspot_1050.png'
import hotspotSprite52 from '../assets/spritesheets/hotspot/hotspot_1051.png'
import hotspotSprite53 from '../assets/spritesheets/hotspot/hotspot_1052.png'
import hotspotSprite54 from '../assets/spritesheets/hotspot/hotspot_1053.png'
import hotspotSprite55 from '../assets/spritesheets/hotspot/hotspot_1054.png'
import hotspotSprite56 from '../assets/spritesheets/hotspot/hotspot_1055.png'
import hotspotSprite57 from '../assets/spritesheets/hotspot/hotspot_1056.png'
import hotspotSprite58 from '../assets/spritesheets/hotspot/hotspot_1057.png'
import hotspotSprite59 from '../assets/spritesheets/hotspot/hotspot_1058.png'
import hotspotSprite60 from '../assets/spritesheets/hotspot/hotspot_1059.png'
import hotspotSprite61 from '../assets/spritesheets/hotspot/hotspot_1060.png'
import hotspotSprite62 from '../assets/spritesheets/hotspot/hotspot_1061.png'
import hotspotSprite63 from '../assets/spritesheets/hotspot/hotspot_1062.png'
import hotspotSprite64 from '../assets/spritesheets/hotspot/hotspot_1063.png'
import hotspotSprite65 from '../assets/spritesheets/hotspot/hotspot_1064.png'
import hotspotSprite66 from '../assets/spritesheets/hotspot/hotspot_1065.png'
import hotspotSprite67 from '../assets/spritesheets/hotspot/hotspot_1066.png'
import hotspotSprite68 from '../assets/spritesheets/hotspot/hotspot_1067.png'
import hotspotSprite69 from '../assets/spritesheets/hotspot/hotspot_1068.png'
import hotspotSprite70 from '../assets/spritesheets/hotspot/hotspot_1069.png'
import hotspotSprite71 from '../assets/spritesheets/hotspot/hotspot_1070.png'
import hotspotSprite72 from '../assets/spritesheets/hotspot/hotspot_1071.png'
import hotspotSprite73 from '../assets/spritesheets/hotspot/hotspot_1072.png'
import hotspotSprite74 from '../assets/spritesheets/hotspot/hotspot_1073.png'
import hotspotSprite75 from '../assets/spritesheets/hotspot/hotspot_1074.png'
import hotspotSprite76 from '../assets/spritesheets/hotspot/hotspot_1075.png'
import hotspotSprite77 from '../assets/spritesheets/hotspot/hotspot_1076.png'
import hotspotSprite78 from '../assets/spritesheets/hotspot/hotspot_1077.png'
import hotspotSprite79 from '../assets/spritesheets/hotspot/hotspot_1078.png'
import hotspotSprite80 from '../assets/spritesheets/hotspot/hotspot_1079.png'
import hotspotSprite81 from '../assets/spritesheets/hotspot/hotspot_1080.png'
import hotspotSprite82 from '../assets/spritesheets/hotspot/hotspot_1081.png'
import hotspotSprite83 from '../assets/spritesheets/hotspot/hotspot_1082.png'
import hotspotSprite84 from '../assets/spritesheets/hotspot/hotspot_1083.png'
import hotspotSprite85 from '../assets/spritesheets/hotspot/hotspot_1084.png'
import hotspotSprite86 from '../assets/spritesheets/hotspot/hotspot_1085.png'
import hotspotSprite87 from '../assets/spritesheets/hotspot/hotspot_1086.png'
import hotspotSprite88 from '../assets/spritesheets/hotspot/hotspot_1087.png'
import hotspotSprite89 from '../assets/spritesheets/hotspot/hotspot_1088.png'
import hotspotSprite90 from '../assets/spritesheets/hotspot/hotspot_1089.png'
import hotspotSprite91 from '../assets/spritesheets/hotspot/hotspot_1090.png'
import hotspotSprite92 from '../assets/spritesheets/hotspot/hotspot_1091.png'
import hotspotSprite93 from '../assets/spritesheets/hotspot/hotspot_1092.png'
import hotspotSprite94 from '../assets/spritesheets/hotspot/hotspot_1093.png'
import hotspotSprite95 from '../assets/spritesheets/hotspot/hotspot_1094.png'
import hotspotSprite96 from '../assets/spritesheets/hotspot/hotspot_1095.png'
import hotspotSprite97 from '../assets/spritesheets/hotspot/hotspot_1096.png'
import hotspotSprite98 from '../assets/spritesheets/hotspot/hotspot_1097.png'
import hotspotSprite99 from '../assets/spritesheets/hotspot/hotspot_1098.png'
import hotspotSprite100 from '../assets/spritesheets/hotspot/hotspot_1099.png'
import hotspotSprite101 from '../assets/spritesheets/hotspot/hotspot_1100.png'
import hotspotSprite102 from '../assets/spritesheets/hotspot/hotspot_1101.png'
import hotspotSprite103 from '../assets/spritesheets/hotspot/hotspot_1102.png'
import hotspotSprite104 from '../assets/spritesheets/hotspot/hotspot_1103.png'
import hotspotSprite105 from '../assets/spritesheets/hotspot/hotspot_1104.png'
import hotspotSprite106 from '../assets/spritesheets/hotspot/hotspot_1105.png'
import hotspotSprite107 from '../assets/spritesheets/hotspot/hotspot_1106.png'
import hotspotSprite108 from '../assets/spritesheets/hotspot/hotspot_1107.png'
import hotspotSprite109 from '../assets/spritesheets/hotspot/hotspot_1108.png'
import hotspotSprite110 from '../assets/spritesheets/hotspot/hotspot_1109.png'
import hotspotSprite111 from '../assets/spritesheets/hotspot/hotspot_1110.png'
import hotspotSprite112 from '../assets/spritesheets/hotspot/hotspot_1111.png'
import hotspotSprite113 from '../assets/spritesheets/hotspot/hotspot_1112.png'
import hotspotSprite114 from '../assets/spritesheets/hotspot/hotspot_1113.png'
import hotspotSprite115 from '../assets/spritesheets/hotspot/hotspot_1114.png'
import hotspotSprite116 from '../assets/spritesheets/hotspot/hotspot_1115.png'
import hotspotSprite117 from '../assets/spritesheets/hotspot/hotspot_1116.png'
import hotspotSprite118 from '../assets/spritesheets/hotspot/hotspot_1117.png'
import hotspotSprite119 from '../assets/spritesheets/hotspot/hotspot_1118.png'
import hotspotSprite120 from '../assets/spritesheets/hotspot/hotspot_1119.png'
import hotspotSprite121 from '../assets/spritesheets/hotspot/hotspot_1120.png'

import values from 'lodash/values'
import keys from 'lodash/keys'
import ListMainMenuItems from '@/graphql/ListMainMenuItem.gql'
import AnimatedClouds from '@/atoms/AnimatedClouds'

// import containerFragment from '../pixi/shader/chromaKeyFragment.glsl'
// import containerVertex from '../pixi/shader/containerVertex.glsl'
import ClickDragTutorial from '@/atoms/ClickDragTutorial'
import CharSplitText from '@/atoms/CharSplitText'

export default {
  name: 'HomePage',

  mixins: [Viewport],

  components: {
    ImagePlayer,
    LoadingSpinner,
    ClickDragTutorial,
    CharSplitText,
    AnimatedClouds
  },

  data () {
    return {
      playerLoading: true,
      playerLoadProgress: 0,
      playerCurrentFrame: 0,
      mouseDown: false,
      introTutorial: true,
      introTimer: true,
      videoLoaded: false,
      playerOffsetX: 0,
      targets: {
        talents: 'Talents',
        illustrators: 'Illustrators',
        socials: 'Socials',
        sponsors: 'Sponsors',
        initiatives: 'Initiatives',
        designers: 'Designers',
        media: 'Media'
      },
      layersToHotspots: null
    }
  },
  created () {
    this.mousePosition = new PIXI.Point(0, 0)
    this.prevMousePosition = this.mousePosition.clone()
    this.playHead = new ElasticNumber(0)
    this.time = new Date().getTime()
  },

  async mounted () {
    if (this.videoLoaded) {
      this.initialize()
    }
  },

  beforeDestroy () {
    RAF.remove(this.render)
  },

  watch: {
    '$root.siteSettings' () {
      this.introTimer = true
      this.initialized = false
      delay(this.initialize, 1000)
    },
    async videoLoaded (value) {
      if (value) {
        this.initialize()
      }
    }
  },

  computed: {
    welcomeText () {
      return this.$root.siteSettings.siteWelcomeText.replace('\\\n', '<br />')
    },
    transparencyFrames () {
      return this.playerFrames === frames.webp ? null : frames.transparency
      // return (this.playerFrames === frames.webp || this.playerFrames === frames.avif) ? null : frames.transparency
    },
    playerFrames () {
      // return frames.jpg
      // return this.$root.platform.ie11 ? frames.jpglow : window.Modernizr.avif ? frames.avif : window.Modernizr.webp ? frames.webp : frames.jpg
      return this.$root.platform.ie11 ? frames.jpglow : window.Modernizr.webp ? frames.webp : frames.jpg
    }
  },

  methods: {
    async initialize () {
      if (this.initialized || this.$root.platform.prerenderer) return
      this.initialized = true
      // await font load
      const font = new FontFaceObserver('Tiempos Fine')
      await font.load()

      const result = await this.$apollo.query({
        query: ListMainMenuItems,
        variables: { status: 'PUBLISHED', sortOn: 'priority' }
      })

      // console.log(result.data.MainMenuItems.items)
      this.layersToHotspots = {}
      result.data.MainMenuItems.items.forEach(item => {
        this.layersToHotspots[item.homepageHotspotName] = item
      })

      delay(() => { this.introTimer = false }, 3000)
      this.createHotspots()
      RAF.add(this.render)

      // delay(, 500)
    },
    createHotspots () {
      // player stopped loading
      if (this.hotspots) {
        values(this.hotspots).forEach(hotspot => {
          this.$refs.player.app.stage.removeChild(hotspot)
        })
      }
      const animatedHotspotTextures = [
        PIXI.Texture.from(hotspotSprite1),
        PIXI.Texture.from(hotspotSprite2),
        PIXI.Texture.from(hotspotSprite3),
        PIXI.Texture.from(hotspotSprite4),
        PIXI.Texture.from(hotspotSprite5),
        PIXI.Texture.from(hotspotSprite6),
        PIXI.Texture.from(hotspotSprite7),
        PIXI.Texture.from(hotspotSprite8),
        PIXI.Texture.from(hotspotSprite9),
        PIXI.Texture.from(hotspotSprite10),
        PIXI.Texture.from(hotspotSprite11),
        PIXI.Texture.from(hotspotSprite12),
        PIXI.Texture.from(hotspotSprite13),
        PIXI.Texture.from(hotspotSprite14),
        PIXI.Texture.from(hotspotSprite15),
        PIXI.Texture.from(hotspotSprite16),
        PIXI.Texture.from(hotspotSprite17),
        PIXI.Texture.from(hotspotSprite18),
        PIXI.Texture.from(hotspotSprite19),
        PIXI.Texture.from(hotspotSprite20),
        PIXI.Texture.from(hotspotSprite21),
        PIXI.Texture.from(hotspotSprite22),
        PIXI.Texture.from(hotspotSprite23),
        PIXI.Texture.from(hotspotSprite24),
        PIXI.Texture.from(hotspotSprite25),
        PIXI.Texture.from(hotspotSprite26),
        PIXI.Texture.from(hotspotSprite27),
        PIXI.Texture.from(hotspotSprite28),
        PIXI.Texture.from(hotspotSprite29),
        PIXI.Texture.from(hotspotSprite30),
        PIXI.Texture.from(hotspotSprite31),
        PIXI.Texture.from(hotspotSprite32),
        PIXI.Texture.from(hotspotSprite33),
        PIXI.Texture.from(hotspotSprite34),
        PIXI.Texture.from(hotspotSprite35),
        PIXI.Texture.from(hotspotSprite36),
        PIXI.Texture.from(hotspotSprite37),
        PIXI.Texture.from(hotspotSprite38),
        PIXI.Texture.from(hotspotSprite39),
        PIXI.Texture.from(hotspotSprite40),
        PIXI.Texture.from(hotspotSprite41),
        PIXI.Texture.from(hotspotSprite42),
        PIXI.Texture.from(hotspotSprite43),
        PIXI.Texture.from(hotspotSprite44),
        PIXI.Texture.from(hotspotSprite45),
        PIXI.Texture.from(hotspotSprite46),
        PIXI.Texture.from(hotspotSprite47),
        PIXI.Texture.from(hotspotSprite48),
        PIXI.Texture.from(hotspotSprite49),
        PIXI.Texture.from(hotspotSprite50),
        PIXI.Texture.from(hotspotSprite51),
        PIXI.Texture.from(hotspotSprite52),
        PIXI.Texture.from(hotspotSprite53),
        PIXI.Texture.from(hotspotSprite54),
        PIXI.Texture.from(hotspotSprite55),
        PIXI.Texture.from(hotspotSprite56),
        PIXI.Texture.from(hotspotSprite57),
        PIXI.Texture.from(hotspotSprite58),
        PIXI.Texture.from(hotspotSprite59),
        PIXI.Texture.from(hotspotSprite60),
        PIXI.Texture.from(hotspotSprite61),
        PIXI.Texture.from(hotspotSprite62),
        PIXI.Texture.from(hotspotSprite63),
        PIXI.Texture.from(hotspotSprite64),
        PIXI.Texture.from(hotspotSprite65),
        PIXI.Texture.from(hotspotSprite66),
        PIXI.Texture.from(hotspotSprite67),
        PIXI.Texture.from(hotspotSprite68),
        PIXI.Texture.from(hotspotSprite69),
        PIXI.Texture.from(hotspotSprite70),
        PIXI.Texture.from(hotspotSprite71),
        PIXI.Texture.from(hotspotSprite72),
        PIXI.Texture.from(hotspotSprite73),
        PIXI.Texture.from(hotspotSprite74),
        PIXI.Texture.from(hotspotSprite75),
        PIXI.Texture.from(hotspotSprite76),
        PIXI.Texture.from(hotspotSprite77),
        PIXI.Texture.from(hotspotSprite78),
        PIXI.Texture.from(hotspotSprite79),
        PIXI.Texture.from(hotspotSprite80),
        PIXI.Texture.from(hotspotSprite81),
        PIXI.Texture.from(hotspotSprite82),
        PIXI.Texture.from(hotspotSprite83),
        PIXI.Texture.from(hotspotSprite84),
        PIXI.Texture.from(hotspotSprite85),
        PIXI.Texture.from(hotspotSprite86),
        PIXI.Texture.from(hotspotSprite87),
        PIXI.Texture.from(hotspotSprite88),
        PIXI.Texture.from(hotspotSprite89),
        PIXI.Texture.from(hotspotSprite90),
        PIXI.Texture.from(hotspotSprite91),
        PIXI.Texture.from(hotspotSprite92),
        PIXI.Texture.from(hotspotSprite93),
        PIXI.Texture.from(hotspotSprite94),
        PIXI.Texture.from(hotspotSprite95),
        PIXI.Texture.from(hotspotSprite96),
        PIXI.Texture.from(hotspotSprite97),
        PIXI.Texture.from(hotspotSprite98),
        PIXI.Texture.from(hotspotSprite99),
        PIXI.Texture.from(hotspotSprite100),
        PIXI.Texture.from(hotspotSprite101),
        PIXI.Texture.from(hotspotSprite102),
        PIXI.Texture.from(hotspotSprite103),
        PIXI.Texture.from(hotspotSprite104),
        PIXI.Texture.from(hotspotSprite105),
        PIXI.Texture.from(hotspotSprite106),
        PIXI.Texture.from(hotspotSprite107),
        PIXI.Texture.from(hotspotSprite108),
        PIXI.Texture.from(hotspotSprite109),
        PIXI.Texture.from(hotspotSprite110),
        PIXI.Texture.from(hotspotSprite111),
        PIXI.Texture.from(hotspotSprite112),
        PIXI.Texture.from(hotspotSprite113),
        PIXI.Texture.from(hotspotSprite114),
        PIXI.Texture.from(hotspotSprite115),
        PIXI.Texture.from(hotspotSprite116),
        PIXI.Texture.from(hotspotSprite117),
        PIXI.Texture.from(hotspotSprite118),
        PIXI.Texture.from(hotspotSprite119),
        PIXI.Texture.from(hotspotSprite120),
        PIXI.Texture.from(hotspotSprite121)
      ]

      this.hotspots = {}
      // console.log(frames)
      values(frames.catalog.layers).forEach((layer, i) => {
        const name = layer.displayName
        if (!this.hotspots[name]) {
          const hotspot = new PIXI.AnimatedSprite(animatedHotspotTextures)
          // hotspot.animationSpeed = 0.5
          hotspot.tint = 0x4e4e4e
          hotspot.animationSpeed = 0// Math.random() * 10
          delay(() => { hotspot.animationSpeed = 0.5 }, i * 200)
          hotspot.alpha = 0
          // hotspot.roundPixels = true
          const hotspotInfo = this.layersToHotspots[name]
          if (!hotspotInfo) return
          // hotspot.href = hotspotInfo.to
          if (hotspotInfo.enabled) {
            hotspot.href = { name: this.targets[hotspotInfo.target] }
          }
          hotspot.play()

          hotspot.accessible = true
          hotspot.accessibleTitle = hotspotInfo[`${this.$language.current}Title`]
          const resolutionMult = 0.5
          const fontSize = 90 * resolutionMult
          const text = new PIXI.Text(hotspotInfo[`${this.$language.current}Title`], {
            fontFamily: 'Tiempos Fine',
            fontSize,
            fill: '#4e4e4e',
            align: 'left'
            // wordWrap: true,
            // wordWrapWidth: 500
          })
          // text.roundPixels = true
          text.position.y = -200
          // text.anchor.set(0, 0.5)
          text.position.x = 50
          text.alpha = this.$root.platform.mobile ? 1 : 0
          text.scale.set(1 / resolutionMult, 1 / resolutionMult)
          hotspot.addChild(text)

          hotspot.on('mouseover', () => {
            // console.log(text.style)
            anime.remove(text)
            anime.remove(text.style)
            if (hotspot.href) {
              anime({
                targets: text,
                alpha: 1,
                duration: 300,
                easing: 'easeInOutQuad'
              })
              // anime({
              //   targets: text.style,
              //   fill: '#e88b98',
              //   duration: 300,
              //   easing: 'easeInOutQuad'
              // })
              // hotspot.play()
            } else {
              anime({
                targets: text,
                alpha: 0.2,
                duration: 300,
                easing: 'easeInOutQuad'
              })
            }
          })

          hotspot.on('mouseout', () => {
            // console.log(text.style)
            // hotspot.gotoAndStop(0)
            anime.remove(text)
            anime.remove(text.style)
            // if (this.$root.platform.mobile) {
            //   anime({
            //     targets: text.style,
            //     fill: '#000000',
            //     duration: 300,
            //     easing: 'easeInOutQuad'
            //   })
            // }
            anime({
              targets: text,
              alpha: this.$root.platform.mobile ? 1 : 0,
              duration: 300,
              easing: 'easeInOutQuad'
            })
          })
          hotspot.on('tap', this.onHotspotClick)
          hotspot.on('click', this.onHotspotClick)
          hotspot.anchor.set(0.5)
          hotspot.interactive = !!hotspot.href
          hotspot.buttonMode = !!hotspot.href
          hotspot.name = name
          hotspot.width = 64
          hotspot.height = 64
          hotspot.isOccluded = true
          hotspot.wasOccluded = true
          hotspot.alpha = 0
          // hotspot.visible = false
          this.hotspots[name] = hotspot
          this.$refs.player.app.stage.addChild(hotspot)

          // add chroma key filter to everything wich is not webp
          // if (this.playerFrames !== frames.webp) {
          //   this.$refs.player.sprite.filters = [new PIXI.Filter(containerVertex, containerFragment, {
          //     transparencyMap: PIXI.Texture.from(frames.transparency[0]),
          //     dimensions: new PIXI.Point(this.viewPort.width, this.viewPort.height)
          //   })]
          // }
        }
      })
    },
    render () {
      const now = new Date().getTime()
      const deltaTime = (now - this.time) / 1000

      const xDelta = this.prevMousePosition.x - this.mousePosition.x
      this.prevMousePosition.copyFrom(this.mousePosition)
      if (this.mouseDown) {
        this.playHead.target -= (xDelta * 0.3)
      }

      if (!this.playerLoading) {
        this.playHead.update(deltaTime)
        // console.log(Math.abs(this.playHead.value - this.playHead.target))
        if (Math.abs(this.playHead.value - this.playHead.target) < 4 && !this.mouseDown) {
          this.playHead.target = this.playHead.value // cut the slow easing to avoid jittering frames
        }
        // console.log(xDelta, this.playHead.value, this.playHead.target)

        this.playerCurrentFrame = this.absmod(Math.round(this.playHead.value), this.playerFrames.length - 1)

        values(this.hotspots).forEach(hp => {
          const isVisible = this.getHotspotOnFrame(hp.name, this.playerCurrentFrame) !== null && !this.introTutorial
          hp.interactive = isVisible
          hp.visible = isVisible
          // hp.isOccluded = !isVisible
          if (isVisible) {
            hp.alpha = this.getHotspotAlpha(hp.name, this.playerCurrentFrame, 10)
          }
        })

        const catalogFrame = frames.catalog.frames[this.playerCurrentFrame]
        const doneNames = []
        if (catalogFrame) {
          keys(catalogFrame).forEach(key => {
            const layer = frames.catalog.layers[key]
            if (layer) {
              const name = layer.displayName
              if (this.hotspots[name]) {
                if (doneNames.indexOf(name) !== -1) {
                  console.warn('Duplicate hotspot tracking detected on frame', this.playerCurrentFrame, 'name:', name, 'key:', key)
                }
                doneNames.push(name)
                const startX = this.$refs.player.sprite.position.x - (this.$refs.player.sprite.width / 2)
                const startY = this.$refs.player.sprite.position.y - (this.$refs.player.sprite.height / 2)
                const posX = this.$refs.player.sprite.width * (parseFloat(catalogFrame[key][0]) / 100)
                const posY = this.$refs.player.sprite.height * (parseFloat(catalogFrame[key][1]) / 100)
                this.hotspots[name].position = new PIXI.Point(Math.round(startX + posX), Math.round(startY + posY))
              }
            }
          })
        } else {
          console.warn(`Missing Frame ${this.playerCurrentFrame} from catalog JSON`)
        }
      }

      const windowAspect = this.viewPort.width / this.viewPort.height

      if (windowAspect < 1.73) {
        this.playerOffsetX = 50 * Math.max(0, 1.27 - windowAspect)
        // console.log(Math.max(0, 1.27 - windowAspect), this.playerOffsetX)
      } else {
        this.playerOffsetX = 0
      }

      this.time = now
    },

    onHotspotClick (e) {
      // console.log('hotspot click', e.target.name)
      const target = e.target || e.currentTarget
      this.$root.soundManager.playClick()
      this.$emit('hotspot:click', target.name)
      if (target.href) {
        this.$router.push({ ...target.href, params: { language: this.$language.current } })
      }
    },

    getMousePositionX (event, touchIndex = 0) {
      if (event.touches && event.touches.length > 0) {
        return event.touches[touchIndex].pageX
      }
      return event.pageX
    },

    getMousePositionY (event, touchIndex = 0) {
      if (event.touches && event.touches.length > 0) {
        return event.touches[touchIndex].pageY
      }
      return event.pageY
    },

    onMouseMove (e) {
      const x = this.getMousePositionX(e)
      const y = this.getMousePositionY(e)
      this.mousePosition.set(x, y)
      if (this.mouseDown) {
        this.introTutorial = false
      }
    },

    onMouseDown (e) {
      this.mouseDown = true
      this.onMouseMove(e)
      this.prevMousePosition.copyFrom(this.mousePosition)
    },

    getHotspotOnFrame (name, frame) {
      const layers = keys(frames.catalog.layers).filter(layerID => frames.catalog.layers[layerID].displayName === name)
      if (layers.length === 0) return false

      const modFrame = this.absmod(frame, this.playerFrames.length - 1)
      for (let i = 0; i < layers.length; i++) {
        const id = layers[i]
        if (frames.catalog.frames[modFrame][id]) return frames.catalog.frames[modFrame][id]
      }
      return null
    },

    getHotspotAlpha (name, currentFrame, transitionLength = 10) {
      let aheadFrameNumber = -1
      for (let i = transitionLength; i > 0; i--) {
        const hotspot = this.getHotspotOnFrame(name, currentFrame + i)
        if (!hotspot) aheadFrameNumber = i
      }

      if (aheadFrameNumber !== -1) {
        const alpha = aheadFrameNumber / transitionLength
        // console.log('ahead alpha', alpha)
        return isNaN(alpha) ? 0 : alpha
      }

      let behindFrameNumber = -1
      for (let i = transitionLength; i > 0; i--) {
        const hotspot = this.getHotspotOnFrame(name, currentFrame - i)
        if (!hotspot) behindFrameNumber = i
      }

      if (behindFrameNumber !== -1) {
        const alpha = behindFrameNumber / transitionLength
        // console.log('behind alpha', alpha)
        return isNaN(alpha) ? 0 : alpha
      }

      return 1
    },
    absmod (n, m) {
      return ((n % m) + m) % m
    }
  },
  metaInfo () {
    return {
      title: 'Home'
    }
  }
}
</script>

<style scoped lang="scss">

.home-page {
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.image-player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  cursor: grab;

  // @include breakpoint('sm-and-down') {
  //   left: 7%;
  // }

  &.dragging {
    cursor: grabbing;
  }
}

.absolute {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  // z-index: -1;
}

.clouds {
  pointer-events: none;
  user-select: none;
  transform-origin: 50% 50%;
  transform: translate3d(0, 10%, 0) scale3d(2, 2, 2);
  opacity: 0.5;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    background-image: url('../assets/letter/clouds.webp');
    background-size: contain;
    background-position: center bottom;

    animation-name: clouds-motion-1;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-duration: 180s;
    animation-timing-function: linear;

    .no-webp & {
      background-image: url('../assets/letter/clouds-min.png');
    }
  }

  &--second {
    transform: translate3d(0, 15%, 0) scale3d(-1.6, 1.6, 1.6);

    &::after {
      animation-name: clouds-motion-1;
      animation-fill-mode: forwards;
      animation-iteration-count: infinite;
      animation-duration: 100s;
      animation-timing-function: linear;
    }
  }
}

.bottom-tutorial {
  position: absolute;
  bottom: 0;
  width: 100%;
}

.initial-tutorial {
  position: absolute;
  pointer-events: none;
  top: 0;
  left: 0;
  background-color: rgba(map-get($shades, 'white'), 0.6);
  width: 100vw;
  height: 100%;
  text-align: center;
}

.v-leave-active,
.v-enter-active {
  .no-prerenderer & {
    transition: transform 1.5s $ease-out-quart, opacity 1.5s $ease-out-quart;
    transform-origin: 50% 50%;
  }
}

.v-enter,
.v-leave-to {
  transform: scale3d(1.2, 1.2, 1.2);
  opacity: 0;
}

</style>
